import React, { useState, useEffect, useRef } from 'react';
import { Tag, ImagePlus, Loader } from 'lucide-react';
import { apiFetch } from '../../pages/marketplace/api';
import { cssUrl } from '@/lib/cssUrl';

/**
 * A vendor's own collections — their category words — each with a cover image.
 *
 * Their category is theirs (a product line: "Storm", "Gaia"). It is not a marketplace shelf; it
 * groups their storefront. A cover makes that storefront read like a real shop.
 */
export function VendorCollectionCovers() {
  const [collections, setCollections] = useState([]);
  const [busy, setBusy] = useState(null);
  const inputs = useRef({});

  const load = () => {
    apiFetch('/vendor/collections')
      .then((d) => setCollections(d.collections || []))
      .catch(() => setCollections([]));
  };

  useEffect(load, []);

  const save = async (name, changes) => {
    const current = collections.find((c) => c.name === name) || {};
    setCollections((prev) => prev.map((c) => (c.name === name ? { ...c, ...changes } : c)));
    await apiFetch('/vendor/collections', {
      method: 'PUT',
      body: JSON.stringify({
        name,
        cover_url: changes.cover_url !== undefined ? changes.cover_url : (current.cover_url || ''),
        blurb: changes.blurb !== undefined ? changes.blurb : (current.blurb || ''),
      }),
    }).catch(() => load());
  };

  const upload = async (name, file) => {
    if (!file) return;
    setBusy(name);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/vendor/product-image', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (data.file_url) await save(name, { cover_url: data.file_url });
    } finally { setBusy(null); }
  };

  if (!collections.length) return null;

  return (
    <div style={{ padding: '0 0 20px' }} data-testid="vendor-collections-manager">
      <p style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
        color: 'var(--ink-light)', margin: '0 0 4px',
      }}>My Collections</p>
      <p style={{ fontSize: 12, color: 'var(--ink-light)', margin: '0 0 12px', lineHeight: 1.5 }}>
        Your own category words, grouped for your storefront. Add a cover so each line looks like
        part of your shop — this is separate from the marketplace shelves.
      </p>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {collections.map((c) => (
          <div key={c.name} style={{
            width: 190, borderRadius: 12, overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.08)', background: '#fff',
          }} data-testid={`vendor-collection-${c.name.toLowerCase().replace(/\s+/g, '-')}`}>
            <div style={{
              height: 96, backgroundColor: 'rgba(91,181,162,0.08)',
              backgroundImage: cssUrl(c.cover_url),
              backgroundSize: 'cover', backgroundPosition: 'center',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {!c.cover_url && <Tag size={18} style={{ color: 'var(--teal)', opacity: 0.5 }} />}
            </div>
            <div style={{ padding: '10px 12px' }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', margin: '0 0 2px' }}>
                {c.name}
              </p>
              <p style={{ fontSize: 11, color: 'var(--ink-light)', margin: '0 0 8px' }}>
                {c.product_count} product{c.product_count !== 1 ? 's' : ''}
              </p>
              <input
                ref={(el) => { inputs.current[c.name] = el; }}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => upload(c.name, e.target.files?.[0])}
              />
              <button
                type="button"
                onClick={() => inputs.current[c.name]?.click()}
                disabled={busy === c.name}
                data-testid={`collection-cover-btn-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
                  padding: '5px 10px', borderRadius: 8, cursor: 'pointer',
                  border: '1px solid rgba(0,0,0,0.12)', background: 'transparent',
                  color: 'var(--ink-mid)',
                }}
              >
                {busy === c.name
                  ? <><Loader size={12} /> Uploading…</>
                  : <><ImagePlus size={12} /> {c.cover_url ? 'Replace cover' : 'Add cover'}</>}
              </button>
              {c.cover_url && (
                <button type="button" onClick={() => save(c.name, { cover_url: '' })}
                  style={{
                    marginLeft: 6, fontSize: 11, background: 'transparent', border: 'none',
                    color: 'var(--ink-light)', cursor: 'pointer', textDecoration: 'underline',
                  }}>Remove</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default VendorCollectionCovers;
